import { useState } from 'react';
import { Badge, Button, Card, FileButton, Group, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUpload, IconX } from '@tabler/icons-react';
import {
  ASSET_DELIVERABLE_MAX_BYTES,
  FACTORY_DELIVERABLE_SLOTS,
  type AssetDeliverableSlot,
  type FactoryAssetPlatform,
} from '@/lib/constants/appFactory';
import { BrandKitUtils } from '../BrandKitUtils';
import { useCreateAssetSet, useFactoryAssetSets } from '../useAppFactory';

interface AppManualSetProps {
  appCfgId: number;
  platform: FactoryAssetPlatform;
  disabled?: boolean;
}

/**
 * The manual arm of the asset pipeline (ADR-0017): per-slot pickers build
 * ONE partial-or-full set that rides the same factory validation → proof
 * sheet → approve-publishes flow as the Brand Kit. Platform-generic since
 * 3.34.0: the slots are the platform's spec rows (Fire TV's splash logo is a
 * bounded mark, not an exact size). Client checks are fast feedback only —
 * the factory re-validates authoritatively, and nothing goes live before
 * Approve & Publish.
 */
export function AppManualSet({ appCfgId, platform, disabled }: AppManualSetProps) {
  const assetSetsQuery = useFactoryAssetSets(appCfgId, platform);
  const createMutation = useCreateAssetSet(appCfgId, platform);
  const [picked, setPicked] = useState<Record<string, File>>({});
  const slots = FACTORY_DELIVERABLE_SLOTS[platform];

  // Gateway off → hide quietly (Brand Kit card behavior). While a set is
  // pending the customer decides THAT set first — one draft at a time.
  if (assetSetsQuery.isError) return null;
  const pending = (assetSetsQuery.data?.assetSets ?? []).some((s) => s.state === 'generated');
  if (pending) return null;

  const handlePick = async (slot: AssetDeliverableSlot, file: File | null) => {
    if (!file) return;
    const problem = await BrandKitUtils.validateSlotFile(file, slot, ASSET_DELIVERABLE_MAX_BYTES);
    if (problem) {
      notifications.show({ title: 'Wrong image', message: problem, color: 'red' });
      return;
    }
    setPicked((prev) => ({ ...prev, [slot.name]: file }));
  };

  const handleClear = (name: string) => {
    setPicked((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async () => {
    let files: Record<string, string>;
    try {
      const entries = await Promise.all(
        Object.entries(picked).map(
          async ([name, file]) => [name, await BrandKitUtils.fileToBase64(file)] as const,
        ),
      );
      files = Object.fromEntries(entries);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Could not read the files.',
        color: 'red',
      });
      return;
    }
    createMutation.mutate(
      { files },
      {
        onSuccess: () => {
          setPicked({});
          notifications.show({
            title: 'Set Uploaded',
            message: 'Review the preview and approve it to publish.',
            color: 'green',
          });
        },
        onError: (err) => {
          notifications.show({ title: 'Error', message: err.message, color: 'red' });
        },
      },
    );
  };

  const groups = [...new Set(slots.map((s) => s.group))];
  const pickedCount = Object.keys(picked).length;

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="md">
        <Group gap="xs">
          <IconUpload size={20} />
          <Text fw={600} size="md">
            Upload Your Own Images
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          Prefer full control? Pick the images you want to replace — you don&apos;t need
          all of them. Sizes must match the ones shown; hover a button to see which screen
          shows that image. You review a preview before anything goes live.
        </Text>

        {groups.map((group) => (
          <Stack key={group} gap={6}>
            <Text size="sm" fw={500}>
              {group}
            </Text>
            <Group gap="xs" wrap="wrap">
              {slots.filter((s) => s.group === group).map((slot) => {
                const chosen = picked[slot.name];
                const size = `${slot.sizeRule === 'max' ? 'up to ' : ''}${slot.w}x${slot.h}${(slot.altSizes ?? []).map((z) => ` or ${z.w}x${z.h}`).join('')}`;
                return (
                  <Group key={slot.name} gap={4}>
                    <FileButton
                      onChange={(file) => void handlePick(slot, file)}
                      accept={slot.format === 'png' ? 'image/png' : 'image/jpeg'}
                    >
                      {(props) => (
                        <Tooltip label={slot.hint} disabled={!slot.hint} withArrow>
                          <Button size="compact-sm" variant={chosen ? 'light' : 'default'} {...props}>
                            {slot.label} · {size}
                          </Button>
                        </Tooltip>
                      )}
                    </FileButton>
                    {chosen && (
                      <Badge
                        variant="light"
                        color="green"
                        size="sm"
                        rightSection={
                          <IconX
                            size={12}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleClear(slot.name)}
                          />
                        }
                      >
                        {chosen.name}
                      </Badge>
                    )}
                  </Group>
                );
              })}
            </Group>
          </Stack>
        ))}

        <Group>
          <Button
            onClick={() => void handleSubmit()}
            loading={createMutation.isPending}
            disabled={disabled || pickedCount === 0}
          >
            Upload {pickedCount > 0 ? `${pickedCount} ` : ''}for Review
          </Button>
          <Text size="xs" c="dimmed">
            Uploads replace only the slots you pick; everything else stays as-is.
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}
